import React, { ReactNode, useState } from 'react';
import { MedisanteTargetSystem } from '../../types';
import {
  Card,
  CardHeader,
  IconButton,
  makeStyles,
  Theme,
  Menu,
  MenuItem,
  Grid,
  CardContent,
  FormControlLabel,
  Switch,
  Divider,
} from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import TargetSystemStatusIcons from '../TargetSystemStatusIcons/TargetSystemStatusIcons';
import { TargetSystemDeviceInfo } from '../../config';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { isDarkTheme } from '../../../../eliot-components/src/theme';
import { useAuthUser } from '../../../../contexts/AuthProvider/hooks';
import {
  Role,
  hasOneOfGlobalRoles,
  hasOneOfOrgRoles,
} from '../../../../contexts/AuthProvider/types';

const useStyles = makeStyles((theme: Theme) => ({
  pageHeader: {
    marginTop: theme.spacing(2),
  },
  tsCards: {
    marginBottom: theme.spacing(11),
  },
  tsCard: {
    marginBottom: theme.spacing(1),
    '&.selected': {
      backgroundColor: theme.palette.action.selected,
    },
    '& .MuiCardHeader-content': {
      overflow: 'hidden',

      '& .MuiCardHeader-title': {
        fontSize: theme.typography.fontSize,
        maxWidth: '100%',
      },
      '& .MuiCardHeader-subheader': {
        fontSize: theme.typography.fontSize,
        textOverflow: 'ellipsis',
        wordBreak: 'break-all',
        overflow: 'hidden !important',
        display: 'inline-block',
        maxWidth: '100%',
      },
    },
  },
  responseIcon: {
    float: 'right',
    marginLeft: 'auto!important',
    marginRight: '8px',
  },
  icons: {
    flexGrow: 1,
    '& .label': {
      marginRight: theme.spacing(1),
    },
  },
  defaultTS: {
    backgroundColor: isDarkTheme(theme)
      ? theme.palette.info.dark
      : theme.palette.info.light + ' !important',
  },
}));

interface SelectedAchor {
  element: null | HTMLElement;
  item: MedisanteTargetSystem | null;
}

interface Props {
  removeDevices: React.Dispatch<React.SetStateAction<string | null>>;
  targetSystems: MedisanteTargetSystem[];
  deviceInfo: (tsid: string) => TargetSystemDeviceInfo;
  onTest: (ts: MedisanteTargetSystem) => void;
  onEdit: (ts: MedisanteTargetSystem) => void;
  updateTargetSystem: (ts: MedisanteTargetSystem) => void;
  removeTargetSystem: React.Dispatch<
    React.SetStateAction<MedisanteTargetSystem | null>
  >;
  disableTargetSystem: (ts: MedisanteTargetSystem) => void;
}

const MobileLayout = (props: Props) => {
  const {
    targetSystems,
    deviceInfo,
    onTest,
    onEdit,
    removeDevices,
    removeTargetSystem,
    updateTargetSystem,
    disableTargetSystem,
  } = props;
  const classes = useStyles();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const setSnack = (snack: string) => {
    if (!!snack) {
      enqueueSnackbar(snack, { variant: 'warning' });
    }
  };
  const { authUser } = useAuthUser();
  const hasPermission = (owner: string) => {
    return (
      hasOneOfGlobalRoles(authUser, [Role.DeviceManager]) ||
      hasOneOfOrgRoles(authUser, [owner], [Role.DeviceManager])
    );
  };
  const hasGlobalPermission = hasOneOfGlobalRoles(authUser, [
    Role.DeviceManager,
  ]);

  let items: ReactNode[] = [];

  const [anchor, setAnchor] = useState<SelectedAchor>({
    element: null,
    item: null,
  });
  const clearAnchor = () => {
    setAnchor({ element: null, item: null });
  };

  items = targetSystems.map((item, index) => {
    let css = [classes.tsCard];

    if (anchor.item && anchor.item.id === item.id) {
      css.push('selected');
    }
    if (item.isDefault) {
      css.push(classes.defaultTS);
    }
    const info = deviceInfo(item.id);
    const getNumberOfDevices = () => {
      let count = info ? info.count : 0;
      return `${count.toLocaleString()} ${count === 1 ? 'device' : 'devices'}`;
    };

    return (
      <Card
        key={index}
        className={css.join(' ')}
        onClick={() => {
          if (info.count > 0) {
            history.push(
              `/devices?target_systems=${encodeURIComponent(item.id)}`
            );
          } else {
            setSnack('No devices to show for the webhook');
          }
        }}
      >
        <CardHeader
          action={
            <IconButton
              role="ts-actions-button"
              onClick={(e) => {
                e.stopPropagation();
                setAnchor({ element: e.currentTarget, item: item });
              }}
            >
              <MoreVertIcon />
            </IconButton>
          }
          title={item.name}
          subheader={item.url}
        />
        <CardContent>
          <Grid
            container
            className={classes.icons}
            alignItems="center"
            wrap="wrap"
          >
            <Grid item className="label">
              Status:
            </Grid>
            <Grid item>
              <TargetSystemStatusIcons
                targetSystem={item}
                incompatibile={info ? info.incompatibile : false}
              />
            </Grid>
          </Grid>

          <br />

          <Grid container>
            <Grid item>{getNumberOfDevices()}</Grid>
          </Grid>

          <br />

          <Grid container>
            <Grid item>
              {item.count ? item.count.toLocaleString() : 0} requests
              <br />
              {item.testCount ? item.testCount.toLocaleString() : 0} test
              requests
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  });

  return (
    <>
      <div className={classes.tsCards}>{items}</div>
      <Menu
        anchorEl={anchor.element}
        onClose={clearAnchor}
        open={Boolean(anchor.element)}
      >
        {anchor.item?.isMutable && (
          <MenuItem disabled={!hasPermission(anchor.item.owner || '')}>
            <FormControlLabel
              control={
                <Switch
                  checked={anchor.item.isDefault}
                  onChange={(e) => {
                    if (anchor && anchor.item) {
                      updateTargetSystem({
                        id: anchor.item.id,
                        isDefault: e.target.checked,
                        name: anchor.item.name,
                        url: anchor.item.url,
                      });
                      clearAnchor();
                    }
                  }}
                  name="isDefault"
                  color="primary"
                />
              }
              label="Set as default"
            />
          </MenuItem>
        )}

        <MenuItem
          disabled={
            !hasPermission(anchor.item?.owner || '') || anchor.item?.deactivated
          }
          onClick={() => {
            clearAnchor();
            anchor && anchor.item != null && onTest(anchor.item);
          }}
        >
          Send test measurements
        </MenuItem>
        {anchor.item?.isMutable && (
          <MenuItem
            disabled={!hasPermission(anchor.item.owner || '')}
            onClick={() => {
              clearAnchor();
              anchor && anchor.item != null && onEdit(anchor.item);
            }}
          >
            Edit webhook
          </MenuItem>
        )}
        {deviceInfo(anchor.item != null ? anchor.item.id : '').count > 0 && (
          <MenuItem
            disabled={!hasPermission(anchor.item?.owner || '')}
            onClick={() => {
              clearAnchor();
              anchor && anchor.item != null && removeDevices(anchor.item.id);
            }}
          >
            Remove devices from webhook
          </MenuItem>
        )}

        {deviceInfo(anchor.item != null ? anchor.item.id : '').count === 0 && (
          <MenuItem
            disabled={!hasPermission(anchor.item?.owner || '')}
            onClick={() => {
              clearAnchor();
              anchor && anchor.item != null && removeTargetSystem(anchor.item);
            }}
          >
            Remove webhook
          </MenuItem>
        )}
        <Divider />
        <MenuItem
          disabled={!hasGlobalPermission}
          onClick={() => {
            clearAnchor();
            anchor && anchor.item != null && disableTargetSystem(anchor.item);
          }}
        >
          {anchor.item?.deactivated ? 'Unblock' : 'Block'} Webhook
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            if (!anchor.item) return;
            let ts = anchor.item;
            let txt = `ID: ${ts.id}
Name: ${ts.name}
URL: ${ts.url}
Format: ${ts.format}
Device count: ${ts.deviceCount || 0}
Owner: ${ts.owner}
Last status code: ${ts.lastStatusCode || 'none'}
Auth type: ${ts.authType}
Is default: ${ts.isDefault ? 'yes' : 'no'}
Request count: ${ts.count || 0}
Test request count: ${ts.testCount || 0}
Deactivated: ${ts.deactivated ? 'yes' : 'no'}\n`;
            navigator.clipboard.writeText(txt);
            clearAnchor();
          }}
        >
          Copy to clipboard
        </MenuItem>
      </Menu>
    </>
  );
};

export default MobileLayout;
